import { MSAData, MSASequence, SecondaryStructure, BasePair } from '../types';
import { buildStemColorMap, UNPAIRED_COLOR } from '../tracks/msa/msa-track';
import { computeColumnData, detectSeqType, colorForResidue } from '../tracks/logo/logo-render';

export type ExportFormat = 'svg' | 'png';
export type SequenceCountOption = number | 'all';

export interface ExportImageOptions {
  /** How many of the alignment's sequences (from the top) to render as rows. */
  sequenceCount: SequenceCountOption;
  format: ExportFormat;
  /** Multiplier applied when rasterizing to PNG (SVG output ignores this). */
  pngScale?: number;
  /** Optional title shown at the top of the exported image, e.g. the Rfam identifier. */
  title?: string;
}

export interface BuiltExportSVG {
  svg: string;
  width: number;
  height: number;
}

// Layout constants -----------------------------------------------------

const HEADER_HEIGHT = 40;
const RULER_HEIGHT = 36;
const ARCS_HEIGHT = 90;
const CONSENSUS_HEIGHT = 26;
const LOGO_HEIGHT = 100;
const ROW_HEIGHT = 20;
const TRACK_GAP = 6;
const BOTTOM_PADDING = 20;
const RIGHT_PADDING = 24;
const LABEL_PADDING = 24;
const MIN_LABEL_WIDTH = 140;
// Generous ceiling — labels must never be clipped/overlapped by the sequence
// grid, and font size (hence required width) scales up for long alignments.
const MAX_LABEL_WIDTH = 1400;
const DEFAULT_COL_WIDTH = 20;
const MIN_COL_WIDTH = 3;
const MIN_ROW_HEIGHT = 6;
// Browsers cap canvas/SVG raster dimensions somewhere around 16384px.
const MAX_CONTENT_DIMENSION = 15000;

// Text (and the row height that contains it) scales up for alignments with
// more positions than this, so labels stay a consistent fraction of the
// overall image width regardless of alignment length — otherwise a longer
// alignment produces a proportionally wider image whose fixed-size text
// reads smaller once a consumer scales the image down to fit a page.
const BASELINE_POSITIONS = 100;
const MAX_FONT_SCALE = 2.5;
const MIN_FONT_PX = 8;

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function resolveSequenceCount(option: SequenceCountOption, total: number): number {
  if (option === 'all') return total;
  return Math.max(0, Math.min(option, total));
}

/** Options a caller can offer the user, capped to what the alignment actually has. */
export function getAvailableSequenceCountOptions(totalSequences: number): SequenceCountOption[] {
  const candidates: number[] = [10, 50, 100];
  const options: SequenceCountOption[] = candidates.filter((n) => n < totalSequences);
  options.push('all');
  return options;
}

function niceTickInterval(numPositions: number, maxTicks: number): number {
  const rough = Math.max(1, Math.ceil(numPositions / maxTicks));
  const steps = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000];
  for (const step of steps) {
    if (step >= rough) return step;
  }
  return steps[steps.length - 1];
}

function buildRuler(marginLeft: number, y: number, numPositions: number, colWidth: number, rulerHeight: number, fontScale: number): string {
  const width = numPositions * colWidth;
  const baselineY = y + rulerHeight - 8;
  const interval = niceTickInterval(numPositions, 40);
  const fontSize = Math.max(MIN_FONT_PX, 14 * fontScale).toFixed(1);
  const parts: string[] = [
    `<line x1="${marginLeft}" y1="${baselineY}" x2="${marginLeft + width}" y2="${baselineY}" stroke="#999" stroke-width="1"/>`,
  ];

  for (let pos = 1; pos <= numPositions; pos += interval) {
    const x = marginLeft + (pos - 0.5) * colWidth;
    parts.push(
      `<line x1="${x.toFixed(1)}" y1="${(baselineY - 6).toFixed(1)}" x2="${x.toFixed(1)}" y2="${baselineY.toFixed(1)}" stroke="#999" stroke-width="1"/>`,
      `<text x="${x.toFixed(1)}" y="${(baselineY - 9).toFixed(1)}" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="${fontSize}" fill="#555">${pos}</text>`
    );
  }
  return parts.join('');
}

/** Cubic-bezier arcs matching nightingale-links' own `arc()` geometry. */
function buildArcs(marginLeft: number, y: number, basePairs: BasePair[], colWidth: number): string {
  const r = ARCS_HEIGHT * 0.6;
  const parts: string[] = [];
  for (const { x: p1, y: p2 } of basePairs) {
    const x1 = marginLeft + (p1 - 0.5) * colWidth;
    const x2 = marginLeft + (p2 - 0.5) * colWidth;
    const baseY = y + ARCS_HEIGHT - 4;
    const topY = baseY - r;
    parts.push(
      `<path d="M ${x1.toFixed(1)} ${baseY.toFixed(1)} C ${(x1 - colWidth).toFixed(1)} ${topY.toFixed(1)}, ${(x2 + colWidth).toFixed(1)} ${topY.toFixed(1)}, ${x2.toFixed(1)} ${baseY.toFixed(1)}" fill="transparent" stroke="rgba(70,130,180,0.5)" stroke-width="1"/>`
    );
  }
  return parts.join('');
}

function buildConsensusRow(marginLeft: number, y: number, consensus: string, numPositions: number, colWidth: number, consensusHeight: number, fontScale: number): string {
  const parts: string[] = [];
  const showText = colWidth >= 8;
  const fontSize = Math.max(MIN_FONT_PX, Math.min(12 * fontScale, consensusHeight - 4));
  for (let i = 0; i < numPositions; i++) {
    const x = marginLeft + i * colWidth;
    const char = consensus[i] ?? '.';
    parts.push(`<rect x="${x.toFixed(1)}" y="${y}" width="${colWidth.toFixed(1)}" height="${consensusHeight}" fill="#f0f0f0" stroke="#ffffff" stroke-width="0.5"/>`);
    if (showText) {
      parts.push(
        `<text x="${(x + colWidth / 2).toFixed(1)}" y="${(y + consensusHeight / 2 + fontSize / 3).toFixed(1)}" text-anchor="middle" font-family="Courier New,Monaco,monospace" font-size="${fontSize.toFixed(1)}" fill="#333">${escapeXml(char)}</text>`
      );
    }
  }
  return parts.join('');
}

function buildLogo(marginLeft: number, y: number, allSequences: MSASequence[], numPositions: number, colWidth: number): string {
  const seqType = detectSeqType(allSequences);
  const CHAR_BASE_WIDTH = 8;
  const FONT_SIZE = 12;
  const CAP_RATIO = 0.72;
  const MIN_LETTER_HEIGHT = 1;
  const parts: string[] = [];

  for (let pos = 1; pos <= numPositions; pos++) {
    const colData = computeColumnData(allSequences, seqType, pos);
    const centerX = marginLeft + (pos - 0.5) * colWidth;
    let stackY = y + LOGO_HEIGHT;

    for (const item of colData) {
      const letterHeight = item.heightFraction * LOGO_HEIGHT;
      if (letterHeight < MIN_LETTER_HEIGHT) continue;

      stackY -= letterHeight;
      const scaleX = colWidth / CHAR_BASE_WIDTH;
      const scaleY = letterHeight / (FONT_SIZE * CAP_RATIO);
      const color = colorForResidue(item.nucleotide, seqType);
      const baselineY = stackY + letterHeight;

      parts.push(
        `<text transform="translate(${centerX.toFixed(2)},${baselineY.toFixed(2)}) scale(${scaleX.toFixed(3)},${scaleY.toFixed(3)})" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="${FONT_SIZE}" font-weight="bold" fill="${color}">${item.nucleotide}</text>`
      );
    }
  }
  return parts.join('');
}

function buildMsaGrid(
  marginLeft: number,
  y: number,
  sequences: MSASequence[],
  numPositions: number,
  colWidth: number,
  rowHeight: number,
  stemColors: string[] | undefined,
  residueFontSize: number,
  labelFontSize: number
): string {
  const parts: string[] = [];
  const showLetters = colWidth >= 8 && rowHeight >= 10;

  sequences.forEach((seq, rowIndex) => {
    const rowY = y + rowIndex * rowHeight;

    // Sequence label
    parts.push(
      `<text x="8" y="${(rowY + rowHeight / 2 + labelFontSize / 3).toFixed(1)}" font-family="Courier New,Monaco,monospace" font-size="${labelFontSize.toFixed(1)}" font-weight="600" fill="#0b5fa5">${escapeXml(seq.name)}</text>`
    );

    for (let i = 0; i < numPositions; i++) {
      const char = seq.sequence[i] ?? '-';
      const x = marginLeft + i * colWidth;
      const bg = stemColors ? (stemColors[i] ?? UNPAIRED_COLOR) : UNPAIRED_COLOR;
      if (bg !== UNPAIRED_COLOR) {
        parts.push(`<rect x="${x.toFixed(1)}" y="${rowY}" width="${colWidth.toFixed(1)}" height="${rowHeight}" fill="${bg}"/>`);
      }
      if (showLetters && char !== '-' && char !== '.') {
        parts.push(
          `<text x="${(x + colWidth / 2).toFixed(1)}" y="${(rowY + rowHeight / 2 + residueFontSize / 3).toFixed(1)}" text-anchor="middle" font-family="Courier New,Monaco,monospace" font-size="${residueFontSize.toFixed(1)}" font-weight="600" fill="#000">${escapeXml(char)}</text>`
        );
      }
    }
  });
  return parts.join('');
}

function trackLabel(y: number, height: number, text: string, fontSize: number): string {
  return `<text x="4" y="${(y + height / 2 + fontSize / 3).toFixed(1)}" font-family="Arial,Helvetica,sans-serif" font-size="${fontSize.toFixed(1)}" fill="#333">${escapeXml(text)}</text>`;
}

/**
 * Build a standalone SVG document of the alignment: ruler, arcs, secondary
 * structure consensus, sequence logo (computed over the FULL sequence set),
 * and the MSA grid for the first `sequenceCount` sequences across every
 * alignment column.
 */
export function buildAlignmentExportSVG(
  msaData: MSAData,
  notation: SecondaryStructure | undefined,
  sequenceLength: number,
  options: ExportImageOptions
): BuiltExportSVG {
  const total = msaData.sequences.length;
  const count = resolveSequenceCount(options.sequenceCount, total);
  const rows = msaData.sequences.slice(0, count);
  const numPositions = Math.max(1, sequenceLength);

  // Longer alignments produce a proportionally wider image; scale text (and
  // the row height that contains it) up to match so labels read at a
  // consistent size whether the alignment is short or long, once a consumer
  // scales the exported image down to fit a page.
  const fontScale = Math.min(MAX_FONT_SCALE, Math.max(1, numPositions / BASELINE_POSITIONS));

  const consensusHeight = CONSENSUS_HEIGHT * fontScale;
  const rulerHeight = RULER_HEIGHT * fontScale;

  const hasArcs = !!notation?.basePairs?.length;
  const hasConsensus = !!notation?.consensus;
  const stemColors = hasConsensus ? buildStemColorMap(notation!.consensus) : undefined;

  // Everything above the MSA grid (header, ruler, arcs, consensus, logo, and
  // the gaps between them) counts against the same dimension ceiling as the
  // grid itself, so the row-count shrink below must budget around it too.
  const preGridHeight =
    HEADER_HEIGHT +
    rulerHeight + TRACK_GAP +
    (hasArcs ? ARCS_HEIGHT + TRACK_GAP : 0) +
    (hasConsensus ? consensusHeight + TRACK_GAP : 0) +
    LOGO_HEIGHT + TRACK_GAP;

  let rowHeight = ROW_HEIGHT * fontScale;
  const gridBudget = MAX_CONTENT_DIMENSION - preGridHeight - BOTTOM_PADDING;
  if (rows.length * rowHeight > gridBudget) {
    rowHeight = Math.max(MIN_ROW_HEIGHT, gridBudget / Math.max(1, rows.length));
  }

  // Font sizes are derived once here (rather than re-derived per track) so
  // the label column width below always matches what actually gets drawn.
  const labelFontSize = Math.max(MIN_FONT_PX, Math.min(16 * fontScale, rowHeight - 4));
  const residueFontSize = Math.max(MIN_FONT_PX, Math.min(14 * fontScale, rowHeight - 4));
  // The consensus track is the shortest one carrying a text label, so it's
  // the binding constraint on how large that shared label font can be.
  const trackLabelFontSize = Math.max(MIN_FONT_PX, Math.min(14 * fontScale, consensusHeight - 6));
  const CHAR_WIDTH_RATIO = 0.62; // approximate monospace advance width per em
  const SANS_CHAR_WIDTH_RATIO = 0.56; // approximate sans-serif advance width per em

  // Size the label column to the longest text actually shown there, at the
  // font size it will actually be rendered at — this includes both the
  // sequence name labels AND the fixed track labels ("Sequence logo in
  // bits", etc.), which use a wider (sans-serif) font and don't get any
  // shorter just because sequence names happen to be short.
  const longestLabel = rows.reduce((max, s) => Math.max(max, s.name.length), 0);
  const trackLabelTexts = [
    hasArcs ? 'Interactive secondary structure' : null,
    hasConsensus ? 'Secondary structure consensus' : null,
    'Sequence logo in bits',
  ].filter((t): t is string => t !== null);
  const widestTrackLabel = trackLabelTexts.reduce((max, t) => Math.max(max, t.length), 0);

  const requiredWidth = Math.max(
    longestLabel * labelFontSize * CHAR_WIDTH_RATIO,
    widestTrackLabel * trackLabelFontSize * SANS_CHAR_WIDTH_RATIO
  );
  const marginLeft = Math.min(MAX_LABEL_WIDTH, Math.max(MIN_LABEL_WIDTH, requiredWidth + LABEL_PADDING));

  // Shrink cell size for very large alignments so the output stays within
  // browser canvas/SVG dimension limits.
  let colWidth = DEFAULT_COL_WIDTH;
  const maxContentWidth = MAX_CONTENT_DIMENSION - marginLeft - RIGHT_PADDING;
  if (numPositions * colWidth > maxContentWidth) {
    colWidth = Math.max(MIN_COL_WIDTH, maxContentWidth / numPositions);
  }

  const width = Math.ceil(marginLeft + numPositions * colWidth + RIGHT_PADDING);

  let y = 0;
  const sections: string[] = [];

  // Header
  const title = options.title
    ? `${options.title} — ${count.toLocaleString()} of ${total.toLocaleString()} sequences, ${numPositions.toLocaleString()} positions`
    : `${count.toLocaleString()} of ${total.toLocaleString()} sequences, ${numPositions.toLocaleString()} positions`;
  const titleFontSize = Math.min(16 * fontScale, 28);
  sections.push(
    `<text x="8" y="${(y + HEADER_HEIGHT / 2 + titleFontSize / 3).toFixed(1)}" font-family="Arial,Helvetica,sans-serif" font-size="${titleFontSize.toFixed(1)}" font-weight="700" fill="#111">${escapeXml(title)}</text>`
  );
  y += HEADER_HEIGHT;

  // Ruler
  sections.push(buildRuler(marginLeft, y, numPositions, colWidth, rulerHeight, fontScale));
  y += rulerHeight + TRACK_GAP;

  // Arcs
  if (hasArcs) {
    sections.push(trackLabel(y, ARCS_HEIGHT, 'Interactive secondary structure', trackLabelFontSize));
    sections.push(buildArcs(marginLeft, y, notation!.basePairs!, colWidth));
    y += ARCS_HEIGHT + TRACK_GAP;
  }

  // Secondary structure consensus
  if (hasConsensus) {
    sections.push(trackLabel(y, consensusHeight, 'Secondary structure consensus', trackLabelFontSize));
    sections.push(buildConsensusRow(marginLeft, y, notation!.consensus, numPositions, colWidth, consensusHeight, fontScale));
    y += consensusHeight + TRACK_GAP;
  }

  // Sequence logo (always computed over the full alignment)
  sections.push(trackLabel(y, LOGO_HEIGHT, 'Sequence logo in bits', trackLabelFontSize));
  sections.push(buildLogo(marginLeft, y, msaData.sequences, numPositions, colWidth));
  y += LOGO_HEIGHT + TRACK_GAP;

  // MSA grid
  sections.push(buildMsaGrid(marginLeft, y, rows, numPositions, colWidth, rowHeight, stemColors, residueFontSize, labelFontSize));
  y += rows.length * rowHeight + BOTTOM_PADDING;

  const height = Math.ceil(y);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
    `<rect x="0" y="0" width="${width}" height="${height}" fill="#ffffff"/>` +
    sections.join('') +
    `</svg>`;

  return { svg, width, height };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to rasterize SVG for PNG export'));
    img.src = src;
  });
}

export async function rasterizeSVGToPNG(svg: string, width: number, height: number, scale = 2): Promise<Blob> {
  let effectiveScale = scale;
  if (width * effectiveScale > MAX_CONTENT_DIMENSION || height * effectiveScale > MAX_CONTENT_DIMENSION) {
    effectiveScale = Math.max(1, Math.min(effectiveScale, MAX_CONTENT_DIMENSION / width, MAX_CONTENT_DIMENSION / height));
  }

  const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * effectiveScale));
    canvas.height = Math.max(1, Math.round(height * effectiveScale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode PNG'))), 'image/png');
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function sanitizeForFilename(value: string): string {
  return value.replace(/[^a-z0-9_-]+/gi, '_');
}

export async function exportAlignmentImage(
  msaData: MSAData,
  notation: SecondaryStructure | undefined,
  sequenceLength: number,
  options: ExportImageOptions
): Promise<void> {
  const { svg, width, height } = buildAlignmentExportSVG(msaData, notation, sequenceLength, options);
  const total = msaData.sequences.length;
  const count = resolveSequenceCount(options.sequenceCount, total);
  const baseName = sanitizeForFilename(options.title || 'alignment');
  const filename = `${baseName}_${count}of${total}_seqs`;

  if (options.format === 'svg') {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    downloadBlob(blob, `${filename}.svg`);
  } else {
    const blob = await rasterizeSVGToPNG(svg, width, height, options.pngScale ?? 2);
    downloadBlob(blob, `${filename}.png`);
  }
}
